export interface ApiEnvelope<T> {
  success?: boolean
  data: T
  message?: string
  server_time?: string | null
}

export interface ApiPaginationMeta {
  total?: number
  current_page?: number
  per_page?: number
  last_page?: number
  from?: number | null
  to?: number | null
}

export interface ApiListResponse<T> extends ApiEnvelope<T[]> {
  meta?: ApiPaginationMeta
}

export interface ApiErrorDetail {
  code?: number
  message?: string
}

export interface ApiErrorResponse {
  data?: {
    error?: ApiErrorDetail
    message?: string
  }
  message?: string
}

export type ApiFormPayload = Record<string, unknown> | FormData

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function unwrapApiData<T>(payload: ApiEnvelope<T> | T): T {
  if (isRecord(payload) && 'data' in payload) {
    return payload.data as T
  }

  return payload as T
}

export function unwrapApiList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (isRecord(payload) && Array.isArray(payload.data)) {
    return payload.data as T[]
  }

  return []
}

export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (!isRecord(error)) {
    return fallback
  }

  const data = isRecord(error.data) ? error.data : null
  const detail = data && isRecord(data.error) ? data.error : null
  const detailMessage = detail?.message
  const dataMessage = data?.message
  const rootMessage = error.message

  if (typeof detailMessage === 'string' && detailMessage.trim()) {
    return detailMessage
  }
  if (typeof dataMessage === 'string' && dataMessage.trim()) {
    return dataMessage
  }
  if (typeof rootMessage === 'string' && rootMessage.trim()) {
    return rootMessage
  }

  return fallback
}

export function getApiErrorCode(error: unknown): number | null {
  if (!isRecord(error) || !isRecord(error.data) || !isRecord(error.data.error)) {
    return null
  }

  const code = error.data.error.code
  return typeof code === 'number' ? code : null
}
